/**
 * @fileoverview What Grow thinks a seed packet it could not place is, with one Yes, in the row's change dialog
 * ("Grow thinks this is bee balm (Monarda), a perennial flower in the mint family. File it under Perennial flower?",
 * UnknownPlantCard): Yes places the packet as Grow suggests (lib/acceptSuggestion: under a broad flower parent as its
 * own variety, else the gardener's own plant, made then). Nothing when Grow has nothing to say; the fields ask.
 * Moved out of PacketReview for D78 (2026-10-22), unchanged, so the card's rows have it too.
 *
 * Where: the row's change dialog (lib/usePacketRows).
 * API: POST /plants/identify (free, for a scan read before suggestions were sent), POST /plants/own (on Yes).
 * Words (en/es): unknown.* (UnknownPlantCard's, failed).
 *
 * @module RowSuggestion
 */

import React from 'react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { UnknownPlantCard } from '@/shared/components/plant-fields/UnknownPlantCard';
import type { ScannedPacket } from '@/shared/api/packets';
import { useAcceptSuggestion, usePacketSuggestion } from '../lib/acceptSuggestion';

/** What a packet's suggestion is given. */
interface RowSuggestionProps {
  /** The packet as read. */
  packet: ScannedPacket;
  /** Yes: the packet as it is filed. */
  onAccept: (placed: ScannedPacket) => void;
}

/**
 * What Grow thinks an unplaced packet is, with Yes; nothing when Grow has nothing to say (the fields ask).
 *
 * @param props - RowSuggestionProps.
 */
export const RowSuggestion: React.FC<RowSuggestionProps> = ({ packet, onAccept }) => {
  const { reactTranslate: t } = useTranslation();
  const suggestion = usePacketSuggestion(packet);
  const acceptSuggestion = useAcceptSuggestion();
  const [filing, setFiling] = React.useState(false);
  const [failed, setFailed] = React.useState<string | null>(null);
  if (!suggestion) return null;
  /** Yes: placed as Grow suggests (an own plant is made first). */
  const yes = async (): Promise<void> => {
    setFiling(true);
    setFailed(null);
    try {
      onAccept(await acceptSuggestion(packet, suggestion));
    } catch (error) {
      setFailed(errorMessage(error, t('unknown.failed')));
    } finally {
      setFiling(false);
    }
  };
  return (
    <UnknownPlantCard
      suggestion={suggestion}
      onYes={() => void yes()}
      busy={filing}
      error={failed ? <FailureText text={failed} /> : null}
    />
  );
};
